/**
 * Page /mon-association — espace représentant (citoyen), L-ASSO-1.2.
 *
 * Permet à un habitant de :
 *   - voir ses rattachements (validés + en attente + refusés) ;
 *   - demander un rattachement à une association de l'annuaire (choix asso + fonction) ;
 *   - une fois VALIDÉ, modifier les coordonnées et le RIB de SA fiche
 *     (le nom / RNA / SIRET / domaine restent gérés par la mairie).
 *
 * Haut de page : AppHeader (← Dashboard), comme toutes les pages connectées.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Users, Plus, Mail, Phone, MapPin, FileCheck, Upload, Banknote, Megaphone, ChevronDown } from 'lucide-react';
import { api, ApiException } from '@/lib/api';
import { compressImageIfNeeded } from '@/lib/image-compress';
import { AppHeader } from '@/components/AppHeader';
import { Card, CardBody, Button, Field, Input, Alert, Badge, SectionEyebrow } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { useToast } from '@/components/ui/Toast';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { REPRESENTANT_FONCTIONS, ASSOCIATION_DOMAINES } from '@roronoa-collectivites/shared-types';
import type {
  MonAssociation, AssociationResume, RepresentantFonction, RepresentantStatut, UpsertAssociationInput,
  AssociationDossierDetail, Subvention, AssociationRepresentant, AssociationAnnonce, AssociationPublicationType,
  AssociationPresenceReponse, AssociationPresenceRecap,
} from '@roronoa-collectivites/shared-types';

const PUBLICATION_TYPE_LABEL: Record<AssociationPublicationType, string> = {
  info: 'Info', evenement: 'Événement', brocante: 'Brocante', tombola: 'Tombola', autre: 'Autre',
};

function fonctionLabel(v: string): string {
  return REPRESENTANT_FONCTIONS.find((f) => f.value === v)?.label ?? v;
}
function statutBadge(s: RepresentantStatut) {
  if (s === 'valide') return <Badge variant="success">Validé</Badge>;
  if (s === 'en_attente') return <Badge variant="warning">En attente</Badge>;
  return <Badge variant="urgent">Refusé</Badge>;
}
function formatIban(v: string): string {
  return v.replace(/\s+/g, '').toUpperCase().replace(/(.{4})/g, '$1 ').trim();
}

export function MonAssociationPage() {
  const { toast } = useToast();
  const { confirm, dialogElement } = useConfirmDialog();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mes, setMes] = useState<MonAssociation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [demandeOpen, setDemandeOpen] = useState(false);
  const [annuaire, setAnnuaire] = useState<AssociationResume[]>([]);
  const [choixAssoId, setChoixAssoId] = useState('');
  const [choixFonction, setChoixFonction] = useState<RepresentantFonction>('president');
  const [demandeBusy, setDemandeBusy] = useState(false);
  const [demandeError, setDemandeError] = useState<string | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<UpsertAssociationInput | null>(null);
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Demande de création d'une association
  const [creerOpen, setCreerOpen] = useState(false);
  const [creerForm, setCreerForm] = useState<UpsertAssociationInput>({ nom: '', domaine: 'autre', objet: '' });
  const [creerFonction, setCreerFonction] = useState<RepresentantFonction>('president');
  const [creerBusy, setCreerBusy] = useState(false);
  const [creerError, setCreerError] = useState<string | null>(null);

  // Dernière publication de chaque association (affichée directement dans la carte).
  // Map associationId → dernière annonce (ou null si aucune / erreur).
  const [dernieresAnnonces, setDernieresAnnonces] = useState<Record<string, AssociationAnnonce | null>>({});

  async function charger() {
    setLoading(true); setError(null);
    try {
      const liste = await api.associations.mesAssociations();
      setMes(liste);
      // Pour chaque asso où l'utilisateur est bureau validé, précharger les demandes d'adhésion.
      for (const m of liste) {
        if (m.estBureau) {
          void chargerDemandes(m.association.id);
        }
      }
      // Précharger la dernière publication de chaque asso validée pour l'afficher
      // directement dans la carte (aperçu visuel : date, lieu, image…).
      for (const m of liste) {
        if (m.statut === 'valide') {
          void (async () => {
            try {
              const annonces = await api.associations.annuaire.annonces(m.association.id);
              setDernieresAnnonces((prev) => ({ ...prev, [m.association.id]: annonces[0] ?? null }));
            } catch {
              setDernieresAnnonces((prev) => ({ ...prev, [m.association.id]: null }));
            }
          })();
        }
      }
      return liste;
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Erreur de chargement');
      return [];
    } finally { setLoading(false); }
  }
  useEffect(() => {
    void charger().then((liste) => {
      // Deep-link depuis une notification d'annonce : ?asso=<id> ouvre directement
      // les annonces de l'association concernée.
      const assoParam = searchParams.get('asso');
      if (assoParam && liste.some((m) => m.association.id === assoParam)) {
        void ouvrirAnnonces(assoParam, liste);
        searchParams.delete('asso');
        setSearchParams(searchParams, { replace: true });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function ouvrirDemande() {
    setDemandeError(null); setChoixAssoId(''); setChoixFonction('president');
    try {
      const list = await api.associations.list({ actif: true });
      setAnnuaire(list);
      setDemandeOpen(true);
    } catch (e) {
      toast.error(e instanceof ApiException ? e.message : 'Impossible de charger l\'annuaire');
    }
  }

  async function envoyerDemande() {
    if (!choixAssoId) { setDemandeError('Choisissez une association.'); return; }
    setDemandeBusy(true); setDemandeError(null);
    try {
      await api.associations.demanderRattachement(choixAssoId, choixFonction);
      toast.success('Demande envoyée à la mairie');
      setDemandeOpen(false);
      await charger();
    } catch (e) {
      setDemandeError(e instanceof ApiException ? e.message : 'Erreur');
    } finally { setDemandeBusy(false); }
  }

  function ouvrirEdition(m: MonAssociation) {
    const a = m.association;
    setEditId(a.id);
    setForm({
      nom: a.nom,
      email: a.email ?? '', telephone: a.telephone ?? '', adresse: a.adresse ?? '',
      codePostal: a.codePostal ?? '', ville: a.ville ?? '', siteWeb: a.siteWeb ?? '',
      ribIban: undefined, ribBic: a.ribBic ?? '', ribTitulaire: a.ribTitulaire ?? '',
    });
    setEditError(null);
    setEditOpen(true);
  }

  async function enregistrer() {
    if (!editId || !form) return;
    setEditBusy(true); setEditError(null);
    try {
      await api.associations.update(editId, form);
      toast.success('Coordonnées mises à jour');
      setEditOpen(false);
      await charger();
    } catch (e) {
      setEditError(e instanceof ApiException ? e.message : 'Erreur');
    } finally { setEditBusy(false); }
  }

  const setF = (patch: Partial<UpsertAssociationInput>) => setForm((f) => (f ? { ...f, ...patch } : f));
  const setCF = (patch: Partial<UpsertAssociationInput>) => setCreerForm((f) => ({ ...f, ...patch }));

  async function envoyerCreation() {
    if (!creerForm.nom || creerForm.nom.trim().length < 2) { setCreerError('Le nom doit comporter au moins 2 caractères.'); return; }
    setCreerBusy(true); setCreerError(null);
    try {
      await api.associations.demanderCreation({ ...creerForm, maFonction: creerFonction });
      toast.success('Demande de création envoyée à la mairie');
      setCreerOpen(false);
      setCreerForm({ nom: '', domaine: 'autre', objet: '' });
      await charger();
    } catch (e) {
      setCreerError(e instanceof ApiException ? e.message : 'Erreur');
    } finally { setCreerBusy(false); }
  }

  // Le bureau (président/trésorier/secrétaire) validé peut ajouter des représentants.
  const [repEmail, setRepEmail] = useState('');
  const [repFonction, setRepFonction] = useState<RepresentantFonction>('tresorier');
  const [repBusy, setRepBusy] = useState(false);
  async function ajouterRepr(assoId: string) {
    if (!repEmail.trim()) { toast.error('Renseignez un email.'); return; }
    setRepBusy(true);
    try {
      await api.associations.ajouterRepresentant(assoId, repEmail.trim(), repFonction);
      toast.success('Représentant ajouté');
      setRepEmail('');
      await charger();
    } catch (e) {
      toast.error(e instanceof ApiException ? e.message : 'Erreur');
    } finally { setRepBusy(false); }
  }
  // Demandes d'adhésion à valider par le bureau (étape 3) — par association
  const [demandes, setDemandes] = useState<Record<string, AssociationRepresentant[]>>({});
  const [adhesionBusy, setAdhesionBusy] = useState(false);
  const [membreBusy, setMembreBusy] = useState(false);

  async function changerPoste(assoId: string, repId: string, fonction: RepresentantFonction) {
    setMembreBusy(true);
    try {
      await api.associations.changerPosteMembre(assoId, repId, fonction);
      toast.success('Poste mis à jour');
      await charger();
    } catch (e) {
      toast.error(e instanceof ApiException ? e.message : 'Erreur');
    } finally { setMembreBusy(false); }
  }
  async function retirerMembre(assoId: string, repId: string, nom: string) {
    if (!(await confirm({ title: 'Retirer ce membre ?', description: `${nom} sera retiré·e de l'association.`, confirmLabel: 'Retirer', danger: true }))) return;
    setMembreBusy(true);
    try {
      await api.associations.retirerMembre(assoId, repId);
      toast.success('Membre retiré');
      await charger();
    } catch (e) {
      toast.error(e instanceof ApiException ? e.message : 'Erreur');
    } finally { setMembreBusy(false); }
  }
  async function basculerContactVisible(assoId: string, visible: boolean) {
    setMembreBusy(true);
    try {
      await api.associations.setContactVisible(assoId, visible);
      await charger();
    } catch (e) {
      toast.error(e instanceof ApiException ? e.message : 'Erreur');
    } finally { setMembreBusy(false); }
  }
  async function autoriserPublication(assoId: string, repId: string, peutPublier: boolean) {
    setMembreBusy(true);
    try {
      await api.associations.setMembrePeutPublier(assoId, repId, peutPublier);
      toast.success(peutPublier ? 'Membre autorisé à publier' : 'Autorisation retirée');
      await charger();
    } catch (e) {
      toast.error(e instanceof ApiException ? e.message : 'Erreur');
    } finally { setMembreBusy(false); }
  }
  async function chargerDemandes(assoId: string) {
    try {
      const liste = await api.associations.demandesAdhesion(assoId);
      setDemandes((d) => ({ ...d, [assoId]: liste }));
    } catch { /* non-bureau / pas de demandes : ignore */ }
  }
  async function statuerAdhesion(assoId: string, repId: string, decision: 'valide' | 'refuse') {
    setAdhesionBusy(true);
    try {
      await api.associations.statuerAdhesion(assoId, repId, decision);
      toast.success(decision === 'valide' ? 'Adhésion acceptée' : 'Demande refusée');
      await chargerDemandes(assoId);
      await charger();
    } catch (e) {
      toast.error(e instanceof ApiException ? e.message : 'Erreur');
    } finally { setAdhesionBusy(false); }
  }

  // Dossier de validation (pièces) — par association
  const [dossierOpen, setDossierOpen] = useState(false);
  const [dossierAssoId, setDossierAssoId] = useState<string | null>(null);
  const [dossierDetail, setDossierDetail] = useState<AssociationDossierDetail | null>(null);
  const [dossierBusy, setDossierBusy] = useState(false);
  const [dossierError, setDossierError] = useState<string | null>(null);

  async function ouvrirDossier(assoId: string) {
    setDossierAssoId(assoId); setDossierOpen(true); setDossierError(null); setDossierDetail(null);
    try { setDossierDetail(await api.associations.dossiers.getDossier(assoId)); }
    catch (e) { setDossierError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function deposer(pieceDemandeeId: string, file: File | null) {
    if (!file || !dossierAssoId) return;
    setDossierBusy(true); setDossierError(null);
    try {
      const f = await compressImageIfNeeded(file);
      await api.associations.dossiers.deposerPiece(dossierAssoId, f, pieceDemandeeId);
      toast.success('Pièce déposée');
      setDossierDetail(await api.associations.dossiers.getDossier(dossierAssoId));
    } catch (e) { setDossierError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setDossierBusy(false); }
  }
  async function soumettreDossier() {
    if (!dossierAssoId) return;
    setDossierBusy(true); setDossierError(null);
    try {
      await api.associations.dossiers.soumettre(dossierAssoId);
      toast.success('Dossier transmis à la mairie');
      setDossierDetail(await api.associations.dossiers.getDossier(dossierAssoId));
    } catch (e) { setDossierError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setDossierBusy(false); }
  }

  // Subventions (Lot 2)
  const [subOpen, setSubOpen] = useState(false);
  const [subAssoId, setSubAssoId] = useState<string | null>(null);
  const [subList, setSubList] = useState<Subvention[] | null>(null);
  const [subError, setSubError] = useState<string | null>(null);
  const [subBusy, setSubBusy] = useState(false);
  const [subDetailId, setSubDetailId] = useState<string | null>(null);
  const [subForm, setSubForm] = useState<{ id?: string; nature: 'projet' | 'fonctionnement'; exercice: number; objet: string; description: string; montantDemande: string }>(
    { nature: 'projet', exercice: new Date().getFullYear(), objet: '', description: '', montantDemande: '' });

  async function ouvrirSubventions(assoId: string) {
    setSubAssoId(assoId); setSubOpen(true); setSubError(null); setSubList(null);
    setSubForm({ nature: 'projet', exercice: new Date().getFullYear(), objet: '', description: '', montantDemande: '' });
    try { setSubList(await api.associations.subventions.mesSubventions(assoId)); }
    catch (e) { setSubError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  // Canal de communication (annonces) — accessible directement depuis « Mon association » (étape 3)
  const [annOpen, setAnnOpen] = useState(false);
  const [annAssoId, setAnnAssoId] = useState<string | null>(null);
  const [annPeutPublier, setAnnPeutPublier] = useState(false);
  const [annList, setAnnList] = useState<AssociationAnnonce[] | null>(null);
  const [annTitre, setAnnTitre] = useState('');
  const [annCorps, setAnnCorps] = useState('');
  const [annType, setAnnType] = useState<AssociationPublicationType>('info');
  const [annAvecPresence, setAnnAvecPresence] = useState(false);
  const [presenceBusy, setPresenceBusy] = useState<string | null>(null);
  const [recapAnnonceId, setRecapAnnonceId] = useState<string | null>(null);
  const [recap, setRecap] = useState<AssociationPresenceRecap | null>(null);
  const [annDate, setAnnDate] = useState('');
  const [annLieu, setAnnLieu] = useState('');
  const [annHoraires, setAnnHoraires] = useState('');
  const [annFlyer, setAnnFlyer] = useState<File | null>(null);
  const [annBusy, setAnnBusy] = useState(false);
  async function ouvrirAnnonces(assoId: string, listeOverride?: MonAssociation[]) {
    // Bureau ou membre autorisé (Lot 2) peut publier.
    const source = listeOverride ?? mes;
    const lien = source.find((m) => m.association.id === assoId);
    setAnnPeutPublier(!!lien?.peutPublierAnnonce);
    setAnnAssoId(assoId); setAnnOpen(true); setAnnList(null); setAnnTitre(''); setAnnCorps('');
    try { setAnnList(await api.associations.annuaire.annonces(assoId)); } catch { setAnnList([]); }
  }
  async function repondrePresence(annonceId: string, reponse: AssociationPresenceReponse) {
    setPresenceBusy(annonceId);
    try {
      await api.associations.annuaire.repondrePresence(annonceId, reponse);
      if (annAssoId) setAnnList(await api.associations.annuaire.annonces(annAssoId));
      if (recapAnnonceId === annonceId && annAssoId) setRecap(await api.associations.annuaire.recapPresences(annAssoId, annonceId));
    } catch (e) {
      toast.error(e instanceof ApiException ? e.message : 'Erreur');
    } finally { setPresenceBusy(null); }
  }
  async function ouvrirRecap(annonceId: string) {
    if (!annAssoId) return;
    if (recapAnnonceId === annonceId) { setRecapAnnonceId(null); setRecap(null); return; }
    try {
      const r = await api.associations.annuaire.recapPresences(annAssoId, annonceId);
      setRecap(r); setRecapAnnonceId(annonceId);
    } catch (e) {
      toast.error(e instanceof ApiException ? e.message : 'Erreur');
    }
  }
  async function publierAnnonce() {
    if (!annAssoId) return;
    if (!annTitre.trim() || !annCorps.trim()) { toast.error('Renseignez un titre et un message.'); return; }
    setAnnBusy(true);
    try {
      let imageMediaKey: string | null = null;
      if (annFlyer) {
        const flyer = await compressImageIfNeeded(annFlyer);
        const up = await api.associations.annuaire.uploadFlyer(annAssoId, flyer);
        imageMediaKey = up.imageMediaKey;
      }
      await api.associations.annuaire.publierAnnonce(annAssoId, {
        titre: annTitre.trim(), corps: annCorps.trim(),
        typePublication: annType,
        dateEvent: annDate ? new Date(annDate).toISOString() : null,
        lieu: annLieu.trim() || null,
        horaires: annHoraires.trim() || null,
        imageMediaKey,
        avecPresence: annAvecPresence,
      });
      toast.success('Publication ajoutée');
      setAnnTitre(''); setAnnCorps(''); setAnnType('info'); setAnnDate(''); setAnnLieu(''); setAnnHoraires(''); setAnnFlyer(null); setAnnAvecPresence(false);
      setAnnList(await api.associations.annuaire.annonces(annAssoId));
    } catch (e) {
      toast.error(e instanceof ApiException ? e.message : 'Erreur');
    } finally { setAnnBusy(false); }
  }
  function editerSub(s: Subvention) {
    setSubForm({ id: s.id, nature: s.nature, exercice: s.exercice, objet: s.objet, description: s.description ?? '', montantDemande: String(s.montantDemande) });
  }
  async function enregistrerSub() {
    if (!subAssoId) return;
    if (!subForm.objet.trim()) { setSubError('Indiquez l\'objet de la demande.'); return; }
    setSubBusy(true); setSubError(null);
    try {
      const body = {
        associationId: subAssoId, nature: subForm.nature, exercice: Number(subForm.exercice),
        objet: subForm.objet.trim(), description: subForm.description.trim() || null, montantDemande: Number(subForm.montantDemande) || 0,
      };
      if (subForm.id) await api.associations.subventions.update(subForm.id, body);
      else await api.associations.subventions.create(body);
      toast.success('Demande enregistrée');
      setSubForm({ nature: 'projet', exercice: new Date().getFullYear(), objet: '', description: '', montantDemande: '' });
      setSubList(await api.associations.subventions.mesSubventions(subAssoId));
    } catch (e) { setSubError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setSubBusy(false); }
  }
  async function soumettreSub(id: string) {
    if (!subAssoId) return;
    setSubBusy(true); setSubError(null);
    try {
      await api.associations.subventions.soumettre(id);
      toast.success('Demande transmise à la mairie');
      setSubList(await api.associations.subventions.mesSubventions(subAssoId));
    } catch (e) { setSubError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setSubBusy(false); }
  }

  return (
    <div className="min-h-screen text-ink">
      <AppHeader module="Mes associations / Acteurs locaux" accent="asso" />

      <main id="main-content" className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8">
        <div className="mb-6 flex items-start justify-between gap-3">
          <div>
            <SectionEyebrow>Vie associative</SectionEyebrow>
            <h1 className="mt-1 font-display text-xl font-semibold tracking-r-tight text-ink sm:text-2xl">
              Mes associations
            </h1>
            <p className="mt-1 text-sm text-ink-muted">
              Gérez vos rattachements et les informations de vos associations.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => navigate('/associations-annuaire')}>
              <Users className="h-4 w-4" /> Annuaire & acteurs locaux
            </Button>
            <Button variant="ghost" onClick={() => { setCreerError(null); setCreerOpen(true); }}>
              <Plus className="h-4 w-4" /> Créer une association
            </Button>
            <Button variant="asso" onClick={ouvrirDemande}>
              <Plus className="h-4 w-4" /> Rattacher une association
            </Button>
          </div>
        </div>

        {error && <Alert variant="error" className="mb-4">{error}</Alert>}

        {loading ? (
          <Card variant="glass" padded><div className="py-8 text-center text-sm text-ink-subtle">Chargement…</div></Card>
        ) : mes.length === 0 ? (
          <Card variant="glass" padded>
            <div className="flex flex-col items-center gap-3 py-10 text-center">
              <Users className="h-8 w-8 text-ink-subtle" aria-hidden="true" />
              <div className="text-sm text-ink-muted">
                Vous ne représentez aucune association pour l'instant.
              </div>
              <Button variant="asso" onClick={ouvrirDemande}>
                <Plus className="h-4 w-4" /> Demander un rattachement
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-3">
            {mes.map((m) => (
              <Card key={m.representantId} variant="glass" padded>
                <CardBody>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-display text-base font-semibold text-ink">{m.association.nom}</span>
                        {statutBadge(m.statut)}
                        <Badge variant="asso">{fonctionLabel(m.fonction)}</Badge>
                      </div>
                      <div className="mt-1 space-y-0.5 text-xs text-ink-subtle">
                        {m.association.email && <div className="flex items-center gap-1"><Mail className="h-3 w-3" /> {m.association.email}</div>}
                        {m.association.telephone && <div className="flex items-center gap-1"><Phone className="h-3 w-3" /> {m.association.telephone}</div>}
                        {m.association.ville && <div className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {m.association.ville}</div>}
                        {m.association.ribRenseigne && <div>RIB : {m.association.ribIbanMasque}</div>}
                      </div>
                    </div>
                  </div>

                  {m.statut === 'valide' && (
                    <div className="mt-3 space-y-3">
                      {m.association.statutValidation === 'en_attente' && (
                        <div className="text-xs text-warning">Association en attente de validation par la mairie.</div>
                      )}

                      {/* Aperçu de la dernière publication directement dans la carte */}
                      <DernierePublicationPreview
                        annonce={dernieresAnnonces[m.association.id]}
                        loaded={m.association.id in dernieresAnnonces}
                        onVoirTout={() => ouvrirAnnonces(m.association.id)}
                      />

                      {/* ───────────── ADHÉRENT SIMPLE : lecture seule ───────────── */}
                      {!m.estBureau && (
                        <div className="space-y-3">
                          <div className="rounded-r-md border border-border bg-surface/40 p-3 text-xs text-ink-subtle">
                            Vous êtes adhérent·e de cette association. Vous avez accès à sa communication
                            (annonces, vie de l'association). La gestion est réservée au bureau.
                          </div>
                          {/* Communication : annonces de l'asso */}
                          <Button variant="asso" size="sm" onClick={() => ouvrirAnnonces(m.association.id)}>
                            <Megaphone className="h-3.5 w-3.5" /> Annonces & vie de l'association
                          </Button>
                        </div>
                      )}

                      {/* ───────────── BUREAU : gestion complète ───────────── */}
                      {m.estBureau && (
                        <>
                      {/* Composition de l'association (membres validés) + admin */}
                      {(m.association.representants?.length ?? 0) > 0 && (
                        <div className="rounded-r-md border border-border bg-surface/40 p-2.5">
                          <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-ink">
                            <Users className="h-3.5 w-3.5" /> Composition ({m.association.representants!.length})
                          </div>
                          <div className="space-y-1.5">
                            {m.association.representants!.map((r) => (
                              <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 text-xs">
                                <span className="text-ink">{r.userPrenom} {r.userNom}</span>
                                <div className="flex items-center gap-1.5">
                                  {r.fonction === 'membre' && (
                                    <label className="flex items-center gap-1 text-[11px] text-ink-subtle">
                                      <input type="checkbox" className="h-3.5 w-3.5 accent-asso"
                                        checked={!!r.peutPublier} disabled={membreBusy}
                                        onChange={(e) => autoriserPublication(m.association.id, r.id, e.target.checked)} />
                                      publier
                                    </label>
                                  )}
                                  <select
                                    className="h-8 rounded-r-md border border-border bg-surface/60 px-2 text-xs text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-asso"
                                    value={r.fonction} disabled={membreBusy}
                                    onChange={(e) => changerPoste(m.association.id, r.id, e.target.value as RepresentantFonction)}
                                    aria-label="Changer le poste">
                                    {REPRESENTANT_FONCTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                                  </select>
                                  <Button variant="ghost" size="sm" disabled={membreBusy}
                                    className="text-urgent hover:bg-urgent/10 hover:text-urgent"
                                    onClick={() => retirerMembre(m.association.id, r.id, `${r.userPrenom} ${r.userNom}`)}>
                                    Retirer
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Réglage RGPD : exposer le contact aux adhérents */}
                      <label className="flex items-center gap-2 text-xs text-ink-subtle">
                        <input type="checkbox" className="h-4 w-4 accent-asso"
                          checked={!!m.association.contactVisibleAdherents} disabled={membreBusy}
                          onChange={(e) => basculerContactVisible(m.association.id, e.target.checked)} />
                        Rendre le contact de l'association (mail / téléphone) visible des adhérents
                      </label>

                      <Button variant="ghost" size="sm" onClick={() => ouvrirEdition(m)}>
                        Modifier les coordonnées / RIB
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => ouvrirDossier(m.association.id)}>
                        <FileCheck className="h-3.5 w-3.5" /> Dossier & pièces
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => ouvrirSubventions(m.association.id)}>
                        <Banknote className="h-3.5 w-3.5" /> Subventions
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => ouvrirAnnonces(m.association.id)}>
                        <Megaphone className="h-3.5 w-3.5" /> Annonces
                      </Button>
                      <div className="rounded-r-md border border-border bg-surface/40 p-2.5">
                        <div className="mb-2 text-xs font-medium text-ink">Ajouter un représentant (bureau)</div>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto_auto]">
                          <Input value={repEmail} onChange={(e) => setRepEmail(e.target.value)} placeholder="email@compte.fr" />
                          <select
                            className="h-9 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-asso"
                            value={repFonction} onChange={(e) => setRepFonction(e.target.value as RepresentantFonction)}>
                            {REPRESENTANT_FONCTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                          </select>
                          <Button variant="asso" size="sm" onClick={() => ajouterRepr(m.association.id)} disabled={repBusy}>Ajouter</Button>
                        </div>
                      </div>

                      {/* Demandes d'adhésion à valider par le bureau */}
                      {(demandes[m.association.id]?.length ?? 0) > 0 && (
                        <div className="rounded-r-md border border-asso/40 bg-asso/5 p-2.5">
                          <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-ink">
                            <Users className="h-3.5 w-3.5" /> Demandes d'adhésion ({demandes[m.association.id]!.length})
                          </div>
                          <div className="space-y-2">
                            {demandes[m.association.id]!.map((d) => (
                              <div key={d.id} className="flex items-center justify-between gap-2 rounded-r-md bg-surface/60 px-2.5 py-2">
                                <div className="min-w-0 text-xs">
                                  <div className="font-medium text-ink">{d.userPrenom} {d.userNom}</div>
                                  <div className="text-ink-subtle">{d.userEmail} · souhaite : {fonctionLabel(d.fonction)}</div>
                                </div>
                                <div className="flex shrink-0 gap-1.5">
                                  <Button variant="asso" size="sm" disabled={adhesionBusy}
                                    onClick={() => statuerAdhesion(m.association.id, d.id, 'valide')}>Accepter</Button>
                                  <Button variant="ghost" size="sm" disabled={adhesionBusy}
                                    className="text-urgent hover:bg-urgent/10 hover:text-urgent"
                                    onClick={() => statuerAdhesion(m.association.id, d.id, 'refuse')}>Refuser</Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                        </>
                      )}
                    </div>
                  )}
                  {m.statut === 'en_attente' && (
                    <div className="mt-3 text-xs text-warning">Votre demande est en attente de validation par l'association.</div>
                  )}
                  {m.statut === 'refuse' && (
                    <div className="mt-3 text-xs text-urgent">Votre demande a été refusée par l'association.</div>
                  )}
                </CardBody>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Dialog open={demandeOpen} onClose={() => setDemandeOpen(false)} title="Rattacher une association" size="md">
        <div className="space-y-4">
          {demandeError && <Alert variant="error">{demandeError}</Alert>}
          <p className="text-sm text-ink-muted">
            Choisissez l'association que vous représentez et votre fonction. La mairie validera votre demande.
          </p>
          <Field label="Association" htmlFor="dem-asso">
            <select id="dem-asso"
              className="h-9 w-full rounded-r-md border border-border bg-surface/60 px-3 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-asso"
              value={choixAssoId} onChange={(e) => setChoixAssoId(e.target.value)}>
              <option value="">— Choisir —</option>
              {annuaire.map((a) => <option key={a.id} value={a.id}>{a.nom}</option>)}
            </select>
          </Field>
          <Field label="Votre fonction" htmlFor="dem-fonction">
            <select id="dem-fonction"
              className="h-9 w-full rounded-r-md border border-border bg-surface/60 px-3 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-asso"
              value={choixFonction} onChange={(e) => setChoixFonction(e.target.value as RepresentantFonction)}>
              {REPRESENTANT_FONCTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setDemandeOpen(false)} disabled={demandeBusy}>Annuler</Button>
            <Button variant="asso" onClick={envoyerDemande} disabled={demandeBusy}>
              {demandeBusy ? 'Envoi…' : 'Envoyer la demande'}
            </Button>
          </div>
        </div>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} title="Modifier les coordonnées / RIB" size="md">
        {form && (
          <div className="space-y-4">
            {editError && <Alert variant="error">{editError}</Alert>}
            <p className="text-xs text-ink-subtle">
              Le nom, le RNA et le SIRET sont gérés par la mairie. Vous pouvez mettre à jour
              les coordonnées et le RIB.
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Email" htmlFor="mf-email"><Input id="mf-email" type="email" value={form.email ?? ''} onChange={(e) => setF({ email: e.target.value })} /></Field>
              <Field label="Téléphone" htmlFor="mf-tel"><Input id="mf-tel" value={form.telephone ?? ''} onChange={(e) => setF({ telephone: e.target.value })} /></Field>
            </div>
            <Field label="Adresse" htmlFor="mf-adr"><Input id="mf-adr" value={form.adresse ?? ''} onChange={(e) => setF({ adresse: e.target.value })} /></Field>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Field label="Code postal" htmlFor="mf-cp"><Input id="mf-cp" value={form.codePostal ?? ''} onChange={(e) => setF({ codePostal: e.target.value })} /></Field>
              <Field label="Ville" htmlFor="mf-ville"><Input id="mf-ville" value={form.ville ?? ''} onChange={(e) => setF({ ville: e.target.value })} /></Field>
              <Field label="Site web" htmlFor="mf-web"><Input id="mf-web" value={form.siteWeb ?? ''} onChange={(e) => setF({ siteWeb: e.target.value })} /></Field>
            </div>
            <div className="rounded-r-lg border border-border bg-surface/40 p-3">
              <div className="mb-2 text-sm font-medium text-ink">Coordonnées bancaires (RIB)</div>
              <p className="mb-3 text-xs text-ink-subtle">Laissez l'IBAN vide pour ne pas le modifier. Il est chiffré et jamais réaffiché en clair.</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="IBAN" htmlFor="mf-iban"><Input id="mf-iban" value={form.ribIban ?? ''} onChange={(e) => setF({ ribIban: formatIban(e.target.value) })} placeholder="Inchangé" /></Field>
                <Field label="BIC" htmlFor="mf-bic"><Input id="mf-bic" value={form.ribBic ?? ''} onChange={(e) => setF({ ribBic: e.target.value.replace(/\s+/g, '').toUpperCase() })} /></Field>
              </div>
              <Field label="Titulaire du compte" htmlFor="mf-titulaire"><Input id="mf-titulaire" value={form.ribTitulaire ?? ''} onChange={(e) => setF({ ribTitulaire: e.target.value })} /></Field>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setEditOpen(false)} disabled={editBusy}>Annuler</Button>
              <Button variant="asso" onClick={enregistrer} disabled={editBusy}>{editBusy ? 'Enregistrement…' : 'Enregistrer'}</Button>
            </div>
          </div>
        )}
      </Dialog>

      {/* Dialog demande de création d'association */}
      <Dialog open={creerOpen} onClose={() => setCreerOpen(false)} title="Demander la création d'une association" size="md">
        <div className="space-y-4">
          {creerError && <Alert variant="error">{creerError}</Alert>}
          <p className="text-sm text-ink-muted">
            Décrivez votre association. La mairie validera la création. Vous en serez le premier représentant ;
            vous pourrez ajouter les autres membres du bureau ensuite.
          </p>
          <Field label="Nom de l'association" htmlFor="cf-nom" required>
            <Input id="cf-nom" value={creerForm.nom} onChange={(e) => setCF({ nom: e.target.value })} placeholder="Tennis Club de…" />
          </Field>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Domaine" htmlFor="cf-domaine">
              <select id="cf-domaine"
                className="h-9 w-full rounded-r-md border border-border bg-surface/60 px-3 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-asso"
                value={creerForm.domaine} onChange={(e) => setCF({ domaine: e.target.value as UpsertAssociationInput['domaine'] })}>
                {ASSOCIATION_DOMAINES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </Field>
            <Field label="Votre fonction" htmlFor="cf-fonction">
              <select id="cf-fonction"
                className="h-9 w-full rounded-r-md border border-border bg-surface/60 px-3 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-asso"
                value={creerFonction} onChange={(e) => setCreerFonction(e.target.value as RepresentantFonction)}>
                {REPRESENTANT_FONCTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Objet (loi 1901)" htmlFor="cf-objet">
            <textarea id="cf-objet"
              className="min-h-[64px] w-full rounded-r-md border border-border bg-surface/60 px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-asso"
              value={creerForm.objet ?? ''} onChange={(e) => setCF({ objet: e.target.value })} placeholder="But de l'association…" />
          </Field>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Email" htmlFor="cf-email"><Input id="cf-email" type="email" value={creerForm.email ?? ''} onChange={(e) => setCF({ email: e.target.value })} /></Field>
            <Field label="Téléphone" htmlFor="cf-tel"><Input id="cf-tel" value={creerForm.telephone ?? ''} onChange={(e) => setCF({ telephone: e.target.value })} /></Field>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setCreerOpen(false)} disabled={creerBusy}>Annuler</Button>
            <Button variant="asso" onClick={envoyerCreation} disabled={creerBusy}>{creerBusy ? 'Envoi…' : 'Envoyer la demande'}</Button>
          </div>
        </div>
      </Dialog>

      {/* Dialog dossier de validation (citoyen : dépôt des pièces + soumission) */}
      <Dialog open={dossierOpen} onClose={() => setDossierOpen(false)} title="Dossier & pièces de l'association" size="md">
        {dossierError && <Alert variant="error" className="mb-3">{dossierError}</Alert>}
        {!dossierDetail ? (
          <div className="py-6 text-center text-sm text-ink-subtle">Chargement…</div>
        ) : (
          <div className="space-y-4">
            <div className="text-sm text-ink-muted">
              {dossierDetail.statut === 'valide' && 'Dossier validé par la mairie.'}
              {dossierDetail.statut === 'soumis' && 'Dossier transmis, en attente de validation par la mairie.'}
              {dossierDetail.statut === 'refuse' && (
                <span className="text-urgent">Dossier refusé{dossierDetail.motifRefus ? ` : ${dossierDetail.motifRefus}` : ''}. Corrigez et soumettez à nouveau.</span>
              )}
              {dossierDetail.statut === 'brouillon' && 'Déposez les pièces demandées puis transmettez votre dossier.'}
            </div>

            <div className="space-y-2">
              {dossierDetail.piecesDemandees.map((pd) => {
                const dep = dossierDetail.piecesDeposees.find((p) => p.pieceDemandeeId === pd.id);
                return (
                  <div key={pd.id} className="rounded-r-md border border-border bg-surface/40 p-2.5">
                    <div className="flex items-center gap-2 text-sm text-ink">
                      {pd.libelle}
                      {pd.obligatoire && <Badge variant="info">obligatoire</Badge>}
                      {dep && <Badge variant="success">déposée</Badge>}
                    </div>
                    {dep && <div className="mt-0.5 text-xs text-ink-subtle">{dep.nomFichier}</div>}
                    <label className="mt-2 inline-flex cursor-pointer items-center gap-1.5 text-xs text-asso-fg hover:underline">
                      <Upload className="h-3.5 w-3.5" />
                      {dep ? 'Remplacer' : 'Déposer un PDF / image'}
                      <input type="file" accept="application/pdf,image/*" className="hidden"
                        disabled={dossierBusy}
                        onChange={(e) => deposer(pd.id, e.target.files?.[0] ?? null)} />
                    </label>
                  </div>
                );
              })}
            </div>

            {(dossierDetail.statut === 'brouillon' || dossierDetail.statut === 'refuse') && (
              <div className="flex justify-end border-t border-border pt-3">
                <Button variant="asso" onClick={soumettreDossier} disabled={dossierBusy}>
                  {dossierBusy ? 'Envoi…' : 'Transmettre le dossier à la mairie'}
                </Button>
              </div>
            )}
          </div>
        )}
      </Dialog>
      {/* Dialog subventions (citoyen : déposer / suivre) */}
      <Dialog open={subOpen} onClose={() => setSubOpen(false)} title="Subventions de l'association" size="md">
        {subError && <Alert variant="error" className="mb-3">{subError}</Alert>}
        <div className="space-y-4">
          {/* Liste existante */}
          <div className="space-y-2">
            {subList === null ? (
              <div className="py-2 text-center text-sm text-ink-subtle">Chargement…</div>
            ) : subList.length === 0 ? (
              <div className="text-sm text-ink-subtle">Aucune demande pour l'instant.</div>
            ) : subList.map((s) => (
              <div key={s.id} className="rounded-r-md border border-border bg-surface/40 p-2.5">
                <button type="button"
                  className="flex w-full items-center justify-between gap-2 text-left"
                  onClick={() => setSubDetailId((id) => (id === s.id ? null : s.id))}
                  aria-expanded={subDetailId === s.id}>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-ink">{s.objet}</div>
                    <div className="text-xs text-ink-subtle">
                      {s.nature === 'projet' ? 'Projet' : 'Fonctionnement'} · {s.exercice} · demandé {s.montantDemande.toLocaleString('fr-FR')} €
                      {s.montantAccorde != null && ` · accordé ${s.montantAccorde.toLocaleString('fr-FR')} €`}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Badge variant={s.statut === 'versee' || s.statut === 'accordee' ? 'success' : s.statut === 'refusee' ? 'urgent' : s.statut === 'brouillon' ? 'info' : 'warning'}>
                      {{ brouillon: 'Brouillon', soumise: 'Soumise', en_instruction: 'En instruction', accordee: 'Accordée', refusee: 'Refusée', versee: 'Versée' }[s.statut]}
                    </Badge>
                    <ChevronDown className={`h-4 w-4 text-ink-subtle transition-transform ${subDetailId === s.id ? 'rotate-180' : ''}`} />
                  </div>
                </button>

                {/* Détail dépliable de la demande */}
                {subDetailId === s.id && (
                  <div className="mt-2 space-y-1.5 border-t border-border pt-2 text-xs text-ink-subtle">
                    {s.description && <div><span className="text-ink">Description :</span> {s.description}</div>}
                    <div><span className="text-ink">Montant demandé :</span> {s.montantDemande.toLocaleString('fr-FR')} €</div>
                    {s.montantAccorde != null && <div><span className="text-ink">Montant accordé :</span> {s.montantAccorde.toLocaleString('fr-FR')} €</div>}
                    {s.soumiseLe && <div><span className="text-ink">Soumise le :</span> {new Date(s.soumiseLe).toLocaleDateString('fr-FR')}</div>}
                    {s.decideeLe && <div><span className="text-ink">Décision le :</span> {new Date(s.decideeLe).toLocaleDateString('fr-FR')}</div>}
                    {s.referencePaiement && <div><span className="text-ink">Référence paiement :</span> {s.referencePaiement}</div>}
                    {s.statut === 'soumise' && <div className="text-warning">En attente d'instruction par la mairie.</div>}
                    {s.statut === 'en_instruction' && <div className="text-warning">En cours d'instruction par la mairie.</div>}
                  </div>
                )}
                {s.statut === 'refusee' && s.motifDecision && <div className="mt-1 text-xs text-urgent">Motif : {s.motifDecision}</div>}
                {s.statut === 'versee' && s.verseLe && <div className="mt-1 text-xs text-success">Versée le {new Date(s.verseLe).toLocaleDateString('fr-FR')}</div>}
                {(s.statut === 'brouillon' || s.statut === 'refusee') && (
                  <div className="mt-2 flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => editerSub(s)}>Modifier</Button>
                    <Button variant="asso" size="sm" onClick={() => soumettreSub(s.id)} disabled={subBusy}>Transmettre à la mairie</Button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Formulaire création / édition (brouillon) */}
          <div className="space-y-2 border-t border-border pt-3">
            <div className="text-sm font-medium text-ink">{subForm.id ? 'Modifier la demande' : 'Nouvelle demande'}</div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Field label="Nature" htmlFor="sf-nature">
                <select id="sf-nature" className="h-10 w-full rounded-r-md border border-border bg-surface px-3 text-sm text-ink"
                  value={subForm.nature} onChange={(e) => setSubForm((f) => ({ ...f, nature: e.target.value as 'projet' | 'fonctionnement' }))}>
                  <option value="projet">Projet / action</option>
                  <option value="fonctionnement">Fonctionnement global</option>
                </select>
              </Field>
              <Field label="Exercice (année)" htmlFor="sf-ex"><Input id="sf-ex" type="number" value={subForm.exercice} onChange={(e) => setSubForm((f) => ({ ...f, exercice: Number(e.target.value) }))} /></Field>
            </div>
            <Field label="Objet" htmlFor="sf-objet"><Input id="sf-objet" value={subForm.objet} onChange={(e) => setSubForm((f) => ({ ...f, objet: e.target.value }))} placeholder="Ex. Tournoi annuel jeunes" /></Field>
            <Field label="Description" htmlFor="sf-desc"><Input id="sf-desc" value={subForm.description} onChange={(e) => setSubForm((f) => ({ ...f, description: e.target.value }))} placeholder="Détail du projet / besoin" /></Field>
            <Field label="Montant demandé (€)" htmlFor="sf-montant"><Input id="sf-montant" type="number" value={subForm.montantDemande} onChange={(e) => setSubForm((f) => ({ ...f, montantDemande: e.target.value }))} /></Field>
            <div className="flex justify-end gap-2">
              {subForm.id && <Button variant="ghost" size="sm" onClick={() => setSubForm({ nature: 'projet', exercice: new Date().getFullYear(), objet: '', description: '', montantDemande: '' })}>Annuler</Button>}
              <Button variant="asso" size="sm" onClick={enregistrerSub} disabled={subBusy}>{subForm.id ? 'Enregistrer' : 'Créer le brouillon'}</Button>
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog open={annOpen} onClose={() => setAnnOpen(false)} title="Annonces & vie de l'association" size="md">
        <div className="space-y-4">
          {annPeutPublier ? (
            <>
              <p className="text-sm text-ink-subtle">
                Publiez une annonce visible par les habitants abonnés à votre association dans l'annuaire communal.
              </p>
              <div className="space-y-2 rounded-r-md border border-border bg-surface/40 p-3">
                <div className="text-sm font-medium text-ink">Nouvelle publication</div>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Titre" htmlFor="an2-titre"><Input id="an2-titre" value={annTitre} onChange={(e) => setAnnTitre(e.target.value)} placeholder="Ex. Brocante annuelle" /></Field>
                  <Field label="Type" htmlFor="an2-type">
                    <select id="an2-type" value={annType} onChange={(e) => setAnnType(e.target.value as AssociationPublicationType)}
                      className="h-9 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-asso">
                      <option value="info">Info</option>
                      <option value="evenement">Événement</option>
                      <option value="brocante">Brocante</option>
                      <option value="tombola">Tombola</option>
                      <option value="autre">Autre</option>
                    </select>
                  </Field>
                </div>
                <Field label="Message" htmlFor="an2-corps"><Input id="an2-corps" value={annCorps} onChange={(e) => setAnnCorps(e.target.value)} placeholder="Détail de la publication" /></Field>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Date (optionnel)" htmlFor="an2-date"><Input id="an2-date" type="datetime-local" value={annDate} onChange={(e) => setAnnDate(e.target.value)} /></Field>
                  <Field label="Lieu (optionnel)" htmlFor="an2-lieu"><Input id="an2-lieu" value={annLieu} onChange={(e) => setAnnLieu(e.target.value)} placeholder="Salle des fêtes…" /></Field>
                </div>
                <Field label="Horaires (optionnel)" htmlFor="an2-hor"><Input id="an2-hor" value={annHoraires} onChange={(e) => setAnnHoraires(e.target.value)} placeholder="Ex. 9h–18h, RDV 8h30" /></Field>
                <Field label="Flyer / visuel (optionnel)" htmlFor="an2-flyer">
                  <input id="an2-flyer" type="file" accept="image/*" onChange={(e) => setAnnFlyer(e.target.files?.[0] ?? null)}
                    className="block w-full text-xs text-ink-subtle file:mr-3 file:rounded-r-md file:border-0 file:bg-asso/20 file:px-3 file:py-1.5 file:text-xs file:text-asso-fg" />
                </Field>
                <label className="flex items-center gap-2 text-xs text-ink-subtle">
                  <input type="checkbox" className="h-4 w-4 accent-asso"
                    checked={annAvecPresence} onChange={(e) => setAnnAvecPresence(e.target.checked)} />
                  Ouvrir une fiche de présence (les adhérents répondent présent / absent / intéressé)
                </label>
                <div className="flex justify-end">
                  <Button variant="asso" size="sm" onClick={publierAnnonce} disabled={annBusy}>{annBusy ? 'Publication…' : 'Publier'}</Button>
                </div>
              </div>
            </>
          ) : (
            <p className="text-sm text-ink-subtle">
              Retrouvez ici les annonces et la vie de l'association. Seul le bureau peut publier.
            </p>
          )}
          <div className="space-y-2">
            <div className="text-sm font-medium text-ink">Publications</div>
            {annList === null ? (
              <div className="py-2 text-center text-sm text-ink-subtle">Chargement…</div>
            ) : annList.length === 0 ? (
              <div className="text-sm text-ink-subtle">Aucune publication pour l'instant.</div>
            ) : annList.map((a, idx) => (
              <details key={a.id} open={idx === 0} className="group rounded-r-md border border-border bg-surface/40 p-2.5">
                <summary className="flex cursor-pointer items-center justify-between gap-2 list-none">
                  <span className="flex items-center gap-2 min-w-0">
                    <Badge variant="asso">{PUBLICATION_TYPE_LABEL[a.typePublication]}</Badge>
                    <span className="truncate text-sm font-medium text-ink">{a.titre}</span>
                  </span>
                  <ChevronDown className="h-4 w-4 shrink-0 text-ink-subtle transition-transform group-open:rotate-180" />
                </summary>
                <div className="mt-2 space-y-1.5">
                  <p className="text-xs text-ink-muted whitespace-pre-wrap">{a.corps}</p>
                  {a.dateEvent && <div className="text-xs text-ink-subtle">📅 {new Date(a.dateEvent).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' })}</div>}
                  {a.lieu && <div className="text-xs text-ink-subtle">📍 {a.lieu}</div>}
                  {a.horaires && <div className="text-xs text-ink-subtle">🕒 {a.horaires}</div>}
                  {a.imageUrl && <img src={a.imageUrl} alt={a.titre} loading="lazy" referrerPolicy="no-referrer" className="mt-2 max-h-60 w-full rounded-r-md object-contain" />}
                  {a.avecPresence && (
                    <div className="mt-2 space-y-1.5 rounded-r-md border border-asso/30 bg-asso/5 p-2">
                      <div className="text-xs font-medium text-ink">Serez-vous présent·e ?</div>
                      <div className="flex flex-wrap gap-1.5">
                        {([['present', 'Je serai présent·e'], ['interesse', 'Ça m\'intéresse'], ['absent', 'Je ne serai pas là']] as const).map(([val, label]) => (
                          <Button key={val} variant={a.maPresence === val ? 'asso' : 'ghost'} size="sm"
                            disabled={presenceBusy === a.id}
                            onClick={() => repondrePresence(a.id, val)}>
                            {label}
                          </Button>
                        ))}
                      </div>
                      {annPeutPublier && (
                        <button type="button" onClick={() => ouvrirRecap(a.id)}
                          className="text-[11px] font-medium text-asso-fg hover:underline">
                          Voir le récap des réponses (bureau)
                        </button>
                      )}
                      {recapAnnonceId === a.id && recap && (
                        <div className="mt-1 rounded-r-md bg-surface/60 p-2 text-xs">
                          <div className="mb-1 text-ink-subtle">
                            ✅ {recap.present} présent·e·s · ⭐ {recap.interesse} intéressé·e·s · ❌ {recap.absent} absent·e·s
                          </div>
                          <div className="space-y-0.5">
                            {recap.lignes.map((l, i) => (
                              <div key={i} className="flex justify-between gap-2">
                                <span className="text-ink">{l.userPrenom} {l.userNom}</span>
                                <span className="text-ink-subtle">{l.reponse === 'present' ? 'présent·e' : l.reponse === 'interesse' ? 'intéressé·e' : 'absent·e'}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="text-[11px] text-ink-subtle">Publié le {new Date(a.createdAt).toLocaleDateString('fr-FR')}</div>
                </div>
              </details>
            ))}
          </div>
        </div>
      </Dialog>
      {dialogElement}
    </div>
  );
}

// ============================================================================
// DernierePublicationPreview — aperçu de la dernière annonce dans la carte asso
// ============================================================================

const PREVIEW_TYPE_LABEL: Record<AssociationPublicationType, string> = {
  info: 'Info', evenement: 'Événement', brocante: 'Brocante', tombola: 'Tombola', autre: 'Autre',
};

function DernierePublicationPreview({
  annonce,
  loaded,
  onVoirTout,
}: {
  annonce: AssociationAnnonce | null | undefined;
  loaded: boolean;
  onVoirTout: () => void;
}) {
  // Pas encore chargé : on n'affiche rien (évite le flash).
  if (!loaded) return null;
  // Chargé mais aucune publication.
  if (!annonce) {
    return (
      <div className="rounded-r-lg border border-asso/20 bg-asso/5 p-3">
        <div className="text-xs text-ink-subtle">Aucune publication pour l'instant.</div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onVoirTout}
      className="block w-full rounded-r-lg border border-asso/25 bg-asso/5 p-3 text-left transition-colors hover:bg-asso/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-asso"
    >
      <div className="mb-1.5 flex items-center gap-2">
        <Badge variant="asso">{PREVIEW_TYPE_LABEL[annonce.typePublication]}</Badge>
        <span className="truncate text-sm font-semibold text-ink">{annonce.titre}</span>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
        {annonce.imageUrl && (
          <img
            src={annonce.imageUrl}
            alt={annonce.titre}
            loading="lazy"
            referrerPolicy="no-referrer"
            className="h-24 w-full shrink-0 rounded-r-md object-cover sm:w-32"
          />
        )}
        <div className="min-w-0 flex-1 space-y-0.5">
          {annonce.corps && (
            <p className="line-clamp-2 text-xs text-ink-muted whitespace-pre-wrap">{annonce.corps}</p>
          )}
          {annonce.dateEvent && (
            <div className="text-xs text-ink-subtle">
              📅 {new Date(annonce.dateEvent).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' })}
            </div>
          )}
          {annonce.lieu && <div className="text-xs text-ink-subtle">📍 {annonce.lieu}</div>}
          {annonce.horaires && <div className="text-xs text-ink-subtle">🕒 {annonce.horaires}</div>}
        </div>
      </div>
      <div className="mt-2 text-[11px] font-medium text-asso-fg">Voir toutes les annonces →</div>
    </button>
  );
}
